import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { skills, softSkills, profile } from '../data/mock';
import { PencilRuler, Waves, BrainCircuit, Terminal, Users, Globe2, CheckCircle2, Wrench } from 'lucide-react';
import SimulationBg from '../components/SimulationBg';

const categoryMeta = {
  design:      { icon: PencilRuler,   number: '01', accent: '#4A8FD1', bg: 'rgba(30,77,139,0.10)' },
  simulation:  { icon: Waves,         number: '02', accent: '#4BADC8', bg: 'rgba(0,184,212,0.10)' },
  engineering: { icon: BrainCircuit,  number: '03', accent: '#E4A135', bg: 'rgba(245,158,11,0.10)' },
  programming: { icon: Terminal,      number: '04', accent: '#4CB88A', bg: 'rgba(16,185,129,0.10)' },
};

const Skills = () => {
  const { t, language } = useLanguage();

  const categories = [
    { key: 'design', data: skills.design, label: t('skills.design') },
    { key: 'simulation', data: skills.simulation, label: t('skills.simulation') },
    { key: 'engineering', data: skills.engineering, label: t('skills.engineering') },
    { key: 'programming', data: skills.programming, label: t('skills.programming') },
  ];

  return (
    <div>
      {/* Header */}
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <SimulationBg variant="isolines" opacity={0.65} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4BADC8] mb-3">
            <Wrench className="w-4 h-4" />
            <span className="w-8 h-px bg-[#4BADC8]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight">
            {t('skills.title')}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
            {t('skills.subtitle')}
          </p>
        </div>
      </section>

      {/* Categories grid */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map(({ key, data, label }) => {
              const meta = categoryMeta[key];
              const Icon = meta.icon;
              return (
                <div
                  key={key}
                  className="relative bg-[#1A2231] border border-white/10 rounded-lg p-6 transition-colors overflow-hidden"
                  style={{ borderColor: 'rgb(226,232,240)' }}
                >
                  {/* accent top bar */}
                  <div
                    className="absolute top-0 left-0 right-0 h-[3px] rounded-t-lg"
                    style={{ backgroundColor: meta.accent }}
                  />
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: meta.bg }}
                      >
                        <Icon className="w-5 h-5" style={{ color: meta.accent }} />
                      </div>
                      <div>
                        <div
                          className="text-[10px] mono uppercase tracking-widest"
                          style={{ color: meta.accent }}
                        >
                          {meta.number}
                        </div>
                        <h3 className="text-lg font-semibold text-slate-50">
                          {label}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.items.map((s) => (
                      <span
                        key={s.name}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm text-slate-100 hover:border-white/25 transition-colors"
                        data-testid={`skill-chip-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: meta.accent }}
                          aria-hidden="true"
                        />
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tools strip */}
          <div className="mt-12 p-6 bg-white/5 border border-white/10 rounded-lg">
            <div className="text-xs mono uppercase tracking-widest text-slate-500 mb-4">
              {language === 'en' ? 'Complete toolset' : 'Gesamter Werkzeugkasten'}
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ...skills.design.items,
                ...skills.simulation.items,
                ...skills.engineering.items,
                ...skills.programming.items,
              ].map((s) => (
                <span
                  key={s.name}
                  className="mono text-xs px-3 py-1.5 bg-[#1A2231] border border-white/10 rounded text-slate-300"
                >
                  {s.name}
                </span>
              ))}
            </div>
          </div>

          {/* Soft Skills + Languages */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-6">
            {/* Soft skills — span 3 */}
            <div className="md:col-span-3 bg-[#1A2231] border border-white/10 rounded-lg p-6 relative overflow-hidden">
              <div
                className="absolute top-0 left-0 right-0 h-[3px] rounded-t-lg"
                style={{ backgroundColor: '#D95871' }}
              />
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(217,88,113,0.12)' }}
                >
                  <Users className="w-5 h-5" style={{ color: '#D95871' }} />
                </div>
                <div>
                  <div className="text-[10px] mono uppercase tracking-widest" style={{ color: '#D95871' }}>
                    05
                  </div>
                  <h3 className="text-lg font-semibold text-slate-50">
                    {t('skills.soft')}
                  </h3>
                </div>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {softSkills.map((s, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2.5 text-sm text-slate-200"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#D95871] flex-shrink-0 mt-0.5" />
                    <span>{s[language]}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Languages — span 2 */}
            <div className="md:col-span-2 bg-[#1A2231] border border-white/10 rounded-lg p-6 relative overflow-hidden">
              <div
                className="absolute top-0 left-0 right-0 h-[3px] rounded-t-lg"
                style={{ backgroundColor: '#4BADC8' }}
              />
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(75,173,200,0.12)' }}
                >
                  <Globe2 className="w-5 h-5" style={{ color: '#4BADC8' }} />
                </div>
                <div>
                  <div className="text-[10px] mono uppercase tracking-widest" style={{ color: '#4BADC8' }}>
                    06
                  </div>
                  <h3 className="text-lg font-semibold text-slate-50">
                    {t('skills.languages')}
                  </h3>
                </div>
              </div>
              <ul className="space-y-3">
                {profile.languages.map((lang, idx) => (
                  <li key={idx} className="flex items-center justify-between text-sm border-b border-white/5 last:border-b-0 pb-2.5 last:pb-0">
                    <span className="text-slate-200 font-medium">
                      {lang.name[language]}
                    </span>
                    <span className="mono text-xs text-slate-400">
                      {typeof lang.level === 'string' ? lang.level : lang.level[language]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Skills;
